import {
  GetAuthorizedResourcesRequest,
  GetAuthorizedResourcesResponse,
  GetAnnouncementsResponse,
  GetOrganizationRequest,
  GetOrganizationResponse,
  GetOrganizationACEUseCasesRequest,
  GetOrganizationACEUseCasesResponse,
  GetOrganizationUsersRequest,
  GetOrganizationUsersResponse,
  GetOrganizationsUsersRequest,
  GetOrganizationsUsersResponse,
  GetOrganizationsRequest,
  GetOrganizationsResponse,
  GetCurrentWorkdayResponse,
  GetUserPreferencesResponse,
  SaveUserPreferencesRequest,
  PreferenceSection,
  GetOrganizationSubmissionReasonsRequest,
  GetOrganizationSubmissionReasonsResponse,
  ListViewResponse,
  DeleteViewRequest,
  DeleteViewResponse,
  SaveViewRequest,
  SaveViewResponse,
  UpdateViewRequest,
  UpdateViewResponse,
  GetViewDetailsRequest,
  GetViewDetailsResponse,
  SubmitFeedbackRequest,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  UpdateOrganizationRequest,
  DeleteOrganizationRequest,
  UpdateOrganizationACEUseCaseRequest,
} from '@amzn/fox-den-mec-setting';
import { generatePath } from 'react-router-dom';
import { Api } from './Api';

/**
 * Defines API for FoxDenMECSettingLambda
 */
const API = new Api('SettingService');

/**
 * Stores the path of FoxDenMECSettings APIs
 */
export const PATH: Record<string, string> = {
  users: '/organization/:organizationId/users',
  submissionReasons: '/organization/:organizationId/submission-reasons',
  listView: '/view/list',
  saveView: '/view/save',
  updateView: '/view/update',
  deleteView: '/view/delete',
  detailsView: '/view/details',
  authorizedResources: '/bindle/authorized-resources',
  submitFeedback: '/user/feedback',
};

/**
 * This object implements all methods presents in FoxDenMECSettingLambda
 */
export const SettingsApi = {
  getAuthorizedResources(
    request: GetAuthorizedResourcesRequest,
  ): Promise<GetAuthorizedResourcesResponse> {
    return API.get(PATH.authorizedResources, request);
  },
  getAnnouncements(): Promise<GetAnnouncementsResponse> {
    return API.get('/announcements');
  },
  getOrganizations(
    request: GetOrganizationsRequest,
  ): Promise<GetOrganizationsResponse> {
    return API.post('/getOrganizations', request);
  },
  getOrganization(
    request: GetOrganizationRequest,
  ): Promise<GetOrganizationResponse> {
    return API.get('/getOrganization', request);
  },
  getOrganizationACEUseCases(
    request: GetOrganizationACEUseCasesRequest,
  ): Promise<GetOrganizationACEUseCasesResponse> {
    return API.get(`/organization/${request.organizationId}/ace-use-cases`, request);
  },
  getOrganizationSubmissionReasons(
    request: GetOrganizationSubmissionReasonsRequest,
  ): Promise<GetOrganizationSubmissionReasonsResponse> {
    const path = `${generatePath(PATH.submissionReasons, {
      organizationId: request.organizationId,
    })}?jeCategory=${request.jeCategory}&jeCategoryReversing=${
      request.jeCategoryReversing
    }`;
    return API.get(path);
  },
  getOrganizationJECategories(
    request: GetOrganizationSubmissionReasonsRequest,
  ): Promise<GetOrganizationSubmissionReasonsResponse> {
    const path = `${generatePath(PATH.submissionReasons, {
      organizationId: request.organizationId,
    })}`;
    return API.get(path);
  },
  getOrganizationUsers(
    request: GetOrganizationUsersRequest,
  ): Promise<GetOrganizationUsersResponse> {
    const path = `${generatePath(PATH.users, {
      organizationId: request.organizationId,
    })}?operation=${request.operation}`;
    return API.get(path);
  },
  getOrganizationsUsers(
    request: GetOrganizationsUsersRequest,
  ): Promise<GetOrganizationsUsersResponse> {
    return API.post('/organizations/users', request);
  },
  getCurrentWorkday(): Promise<GetCurrentWorkdayResponse> {
    return API.get('/get/close-calendar/current-workday');
  },

  getUserPreferences(hash?: string): Promise<GetUserPreferencesResponse> {
    return API.get(
      '/user/preferences/FoxDenFrontend',
      hash ? { previousHash: hash } : undefined,
    );
  },

  saveUserPreferences(section: PreferenceSection) {
    const request: SaveUserPreferencesRequest = {
      sections: [section],
      service: 'FoxDenFrontend',
    };
    return API.post('/user/preferences', request);
  },
  listView(): Promise<ListViewResponse> {
    return API.get(PATH.listView);
  },
  deleteViews(input: DeleteViewRequest): Promise<DeleteViewResponse> {
    return API.post(PATH.deleteView, input);
  },
  saveView(input: SaveViewRequest): Promise<SaveViewResponse> {
    return API.post(PATH.saveView, input);
  },
  updateView(input: UpdateViewRequest): Promise<UpdateViewResponse> {
    return API.post(PATH.updateView, input);
  },
  getViewDetails(
    input: GetViewDetailsRequest,
  ): Promise<GetViewDetailsResponse> {
    return API.post(PATH.detailsView, input);
  },
  submitFeedback(input: SubmitFeedbackRequest): Promise<void> {
    return API.post(PATH.submitFeedback, input);
  },
  createOrganization(input: CreateOrganizationRequest): Promise<CreateOrganizationResponse> {
    return API.post('/organization', input);
  },
  updateOrganization(input: UpdateOrganizationRequest): Promise<void> {
    return API.put('/organization', input);
  },
  deleteOrganization(input: DeleteOrganizationRequest): Promise<void> {
    return API.delete(`/organization/${input.organizationId}`);
  },
  updateOrganizationACEUseCase(input: UpdateOrganizationACEUseCaseRequest): Promise<void> {
    return API.put('/organization-ace-use-cases', input);
  },
};
